(ns minorodata.pipeline-dag.layout
  "Shared parse + ELK layout for the pipeline DAG, used by BOTH the global
   pipelines page (`minorodata.dependency-graph.events`) and the scoped pipeline
   editor (`minorodata.sandbox.events`).

   The pages differ only in the options handed to `do-layout`:

     :groups?     cluster transform/extract/model nodes into `transform-group` /
                  `source` / `model-group` container nodes (rendered as xyflow
                  parent nodes), each with the tables its members WRITE (see
                  `assign-table-containers`). Off ⇒ a flat graph, laid out by the
                  same machinery minus the clustering.
     :preset      an ELK preset key from `layout-presets`.
     :density     a key from `densities`: node width + the ELK gaps, i.e. how much
                  room the graph takes. Not its shape.
     :table-shape :pill (icon-only, the compact table both pages render — its name
                  lives in a tooltip) or :card (a full node card; the default, kept
                  as the escape hatch back to a labelled table).
     :no-run-history
                  node types that carry no execution history in this view, so
                  their cards drop the last-run row (the pipeline editor's sandbox
                  transforms/extracts — their status comes from the sandbox
                  execution statuses instead).

   Everything else — the algorithm, node heights, edge routing — is shared, so the
   same pipeline reads the same way on both pages.

   Node HEIGHT is derived from how many rows the card renders, and the card reads
   the same `:in-container?` / `:last-run-row?` flags this ns stamps on, so ELK and
   the DOM can't disagree (see
   `minorodata.components.pipeline-dag.nodes/entity-card`):

     transform/extract/model in a container  title + last-run          → 2 rows
     transform/extract/model loose           title + group + last-run  → 3 rows
     table (boxed or loose)                  title + schema            → 2 rows

   A type in `:no-run-history` drops the last-run row, so it loses a row too:

     in a container                          title                     → 1 row
     loose                                   title + group             → 2 rows"
  (:require
   [applied-science.js-interop :as j]
   [bleene.dag.events :refer [elk]]
   [clojure.string :as str]
   [datascript.core :as datascript]
   [minorodata.app.db :refer [conn]]
   [minorodata.pipeline-dag.layout-cache :as layout-cache]
   [minorodata.pipeline-dag.timing :as timing]))


;; =============================================================================
;; Dimensions
;; =============================================================================

(def node-width
  "Default width for every entity node. Titles CSS-ellipsis instead of widening the
   node, so a column of nodes has a straight edge and ELK packs predictably. A
   density may narrow it (see `densities`), but it stays uniform within a page —
   that straight edge is the point."
  400)

;; Card heights are measured off what the card actually RENDERS (see
;; `minorodata.components.pipeline-dag.nodes/entity-card`), because xyflow hangs a
;; node's edge handles at 50% of the height declared HERE. A card TALLER than its
;; declared height therefore attaches its edges above its own visible centre, and
;; overflows the box ELK packed around it — eating the container's bottom padding and
;; the gap to the node below.
(def ^:private card-chrome
  "`node-root`'s `p-3` (12px top + bottom) plus its 1px border on both edges."
  26)

(def ^:private icon-height
  "`tab-icon` — a 24px glyph in a `p-0.5` tile."
  28)

(def ^:private text-row
  "One `leading-4` title / subtitle line."
  16)

(def ^:private footer-row
  "`node-footer` — `mt-1` + `border-t` + `pt-1` above a 16px line, less its `-mb-2`."
  17)

(def pill-size
  "Side of the icon-only table pill (`:table-shape :pill`) — the icon plus the card
   chrome, which `p-3` makes the same on both axes."
  (+ card-chrome icon-height))

;; The icon is taller than a single line of text, so a one-row card is as tall as the
;; ICON, not the line — the same height as the pill, which is that icon alone.
(def one-row-height (+ card-chrome (max icon-height text-row)))
(def two-row-height (+ card-chrome (max icon-height (* 2 text-row))))
;; The third row is the last-run footer, below a two-row body.
(def three-row-height (+ two-row-height footer-row))

(def entity-types
  "Node types that render an entity card (as opposed to a table)."
  #{"transform" "extract" "model"})


;; =============================================================================
;; ELK presets
;; =============================================================================

(def ^:private container-heading-height
  "A container box's heading row — `px-4 py-3` around a `text-xl` (28px) line — plus
   the box's own 2px top border (see
   `minorodata.components.pipeline-dag.nodes/container-view`)."
  54)

(defn- group-padding
  "ELK `elk.padding` for a container box, leaving `gap` px of room on all four sides
   of its members. The top clears the heading FIRST, so the space above the first row
   reads the same as the space beside it — a bare `top=gap` tucks that row under the
   label instead."
  [gap]
  (str "[top=" (+ container-heading-height gap) ",left=" gap ",bottom=" gap ",right=" gap "]"))

;; All presets keep SEPARATE_CHILDREN: our edge structure puts cross-group deps at
;; the root and intra-group deps inside each box, so no edge crosses a hierarchy
;; level. `:root` applies to the graph root, `:group` to each container box,
;; optional `:source-extra` is merged onto the boxes holding INGEST extracts only, and
;; `:separate-groups?` partitions source-less components to the right (see
;; `build-elk-input`).
;;
;; `shared-spacing` holds the values that decide the graph's visual rhythm and
;; edge shape. Edge ROUTING and the algorithm are shared by both pages
;; deliberately — splines vs orthogonal edges made the same pipeline read as two
;; different diagrams. How tightly the same shape packs is a per-page choice, see
;; `densities`.
(def ^:private shared-spacing
  {"elk.edgeRouting"                           "ORTHOGONAL"
   "elk.layered.spacing.nodeNodeBetweenLayers" "120"
   "elk.layered.spacing.edgeNodeBetweenLayers" "40"
   "elk.layered.spacing.edgeEdgeBetweenLayers" "15"
   "elk.spacing.nodeNode"                      "55"
   "elk.spacing.edgeNode"                      "30"
   "elk.spacing.edgeEdge"                      "12"
   "elk.spacing.componentComponent"            "60"
   "elk.spacing.labelNode"                     "0"})

(def layout-presets
  {:sources-left
   {:label "Default"
    :root  (merge shared-spacing
                  {"elk.algorithm"                                      "layered"
                   "elk.direction"                                      "RIGHT"
                   "elk.hierarchyHandling"                              "SEPARATE_CHILDREN"
                   "elk.layered.nodePlacement.strategy"                 "BRANDES_KOEPF"
                   "elk.layered.nodePlacement.bk.fixedAlignment"        "BALANCED"
                   "elk.layered.nodePlacement.favorStraightEdges"       "true"
                   "elk.layered.crossingMinimization.strategy"          "LAYER_SWEEP"
                   "elk.layered.crossingMinimization.greedySwitch.type" "TWO_SIDED"
                   ;; 40 gave a byte-identical layout at ~2x the cost; below 7 it degrades
                   "elk.layered.thoroughness"                           "7"
                   "elk.layered.cycleBreaking.strategy"                 "GREEDY"
                   "elk.layered.mergeEdges"                             "true"
                   "elk.contentAlignment"                               "H_LEFT"
                   "elk.separateConnectedComponents"                    "false"
                   "elk.partitioning.activate"                          "true"})
    :group {"elk.padding"                                (group-padding 24)
            "elk.layered.nodePlacement.strategy"         "BRANDES_KOEPF"
            "elk.layered.nodePlacement.bk.fixedAlignment" "BALANCED"
            "elk.layered.nodePlacement.favorStraightEdges" "true"
            "elk.layered.crossingMinimization.strategy"  "LAYER_SWEEP"
            ;; one shared layering per box so an edge-less node stays in the
            ;; first column instead of being packed beside a chain in column 2
            "elk.separateConnectedComponents"            "false"
            ;; match the between-layer gap to the within-column gap so connected
            ;; nodes aren't pushed far from coincidentally-stacked ones
            "elk.layered.spacing.nodeNodeBetweenLayers"  "40"
            "elk.spacing.nodeNode"                       "40"
            "elk.contentAlignment"                       "V_TOP"}
    ;; Pins the boxes that START a pipeline to the first layer — INGEST extracts only.
    ;; An exporter reads a warehouse table and pushes it out, so it carries no
    ;; constraint and lands wherever its edges put it, like any other node. Pinning it
    ;; first (as this did when it applied to every extract) sat it LEFT of the
    ;; transform feeding it, with its edge doubling back across the graph.
    :source-extra     {"elk.layered.layering.layerConstraint" "FIRST_SEPARATE"}
    :separate-groups? true
    :align-left?      true}

   :compact
   ;; Mirrors the bleene Konva dependency-graph ELK config (see
   ;; `bleene.dag.events/layout-options`) so this preset lays out the same way the
   ;; classic graph does. Kept as an escape hatch; `:sources-left` is the default.
   {:label "Classic"
    :root  {"elk.algorithm"                                  "layered"
            "elk.direction"                                  "RIGHT"
            "elk.edgeRouting"                                "SPLINES"
            "elk.hierarchyHandling"                          "SEPARATE_CHILDREN"
            "elk.layered.compaction.postCompaction.strategy" "DOWN"
            "elk.contentAlignment"                           "H_LEFT"
            "elk.spacing.labelNode"                          "0"
            "elk.spacing.nodeNode"                           "50"
            "elk.spacing.edgeEdge"                           "10"
            "elk.spacing.componentComponent"                 "50"
            "elk.layered.spacing.nodeNodeBetweenLayers"      "500"}
    :group {"elk.padding"                                    "[top=85,left=30,bottom=50,right=30]"
            "elk.layered.compaction.postCompaction.strategy" "bottom"}}})

(def layout-order
  "Stable button order for the layout switcher."
  [:sources-left :compact])

(def default-layout :sources-left)


;; =============================================================================
;; Density
;; =============================================================================

(def densities
  "How much room a laid-out graph takes: the card width handed to `size-node`, plus
   ELK gaps merged onto the chosen preset's `:root` / `:group` options. The preset
   still owns the graph's SHAPE (algorithm, partitioning, edge routing) — a density
   only moves things closer together, so both pages keep reading as the same
   diagram.

     :comfortable  the `node-width` / `shared-spacing` defaults — the global
                   pipelines page, where you pan around the whole warehouse and
                   the boxes need air.
     :dense        narrower cards and the pipeline editor's own gaps from before
                   the layout was hoisted into this ns. The editor is a SCOPED
                   view of one lineage in a panel, so packing tight is what keeps
                   the chain on screen.

   `:node-width` is absent from `:comfortable` on purpose — `size-node` falls back
   to `node-width`, so the default lives in exactly one place."
  {:comfortable {}
   :dense
   {:node-width 300
    :root  {"elk.spacing.nodeNode"                      "20"
            "elk.spacing.edgeNode"                      "15"
            "elk.spacing.edgeEdge"                      "10"
            "elk.spacing.componentComponent"            "50"
            "elk.layered.spacing.nodeNodeBetweenLayers" "40"
            "elk.layered.spacing.edgeNodeBetweenLayers" "20"
            "elk.layered.spacing.edgeEdgeBetweenLayers" "10"}
    ;; All four interior gaps tighten together — `group-padding` clears the box
    ;; heading before the top gap starts, so tightening it doesn't tuck the first row
    ;; under the label. The two node gaps stay MATCHED for the same reason the
    ;; comfortable box matches them.
    :group {"elk.padding"                               (group-padding 16)
            "elk.spacing.nodeNode"                      "20"
            "elk.layered.spacing.nodeNodeBetweenLayers" "20"}}})

(def default-density :comfortable)


;; =============================================================================
;; Parsing
;; =============================================================================

(defn parse-raw-node
  "Convert a backend DAG node into the shape consumed by the React Flow graph.
   Table nodes carry only `:node-id` + `:type`; transform/extract/model nodes also
   carry `:name` and a `:metadata` map with `:group-id`/`:source-id`/`:source-name`/
   `:transform-id`/`:extract-id`/`:model-id`/`:model-type`. The backend labels
   analytics-model nodes `\"model\"`, which the FE (node-types, tab-icon,
   resolve-subtitle) consumes directly.

   The :subtitle is seeded with the raw id; the sub resolves the readable
   group/source name. Dimensions are applied separately by `size-node`, which
   needs to know whether grouping is on."
  [{:keys [node-id type name metadata]}]
  (let [{:keys [group-id source-id source-name transform-id extract-id model-id model-type]} metadata
        table?     (= type "table")
        parts      (when table? (str/split node-id #"\."))
        title      (if table? (last parts) name)
        subtitle   (cond
                     table?               (str/join "." (butlast parts))
                     (= type "transform") group-id
                     (= type "extract")   source-id)]
    {:id node-id
     :position {:x 0 :y 0}
     :targetPosition "left"
     :sourcePosition "right"
     :type type
     :data {:id        node-id
            :group-id  subtitle
            :title     title
            :subtitle  subtitle
            :master-id node-id
            :type      type
            :metadata  (cond-> {}
                         transform-id (assoc :transform-id transform-id)
                         extract-id   (assoc :extract-id extract-id)
                         ;; model nodes carry their instance id; the dependency
                         ;; graph resolves it to a registry model to group by.
                         model-id     (assoc :model-id model-id)
                         model-type   (assoc :model-type model-type)
                         source-name  (assoc :source-name source-name))}}))

(defn parse-raw-edge [edge]
  (let [[source target] (if (sequential? edge)
                          edge
                          [(:source edge) (:target edge)])]
    {:id     (str source "-" target)
     :source source
     :target target}))


;; =============================================================================
;; Containers
;; =============================================================================

(defn container-id [kind group-id]
  (str (name kind) ":" group-id))

(def container-types
  "xyflow node types that render a cluster box rather than an entity."
  #{"transform-group" "source" "model-group"})

(defn container?
  "True for a laid-out container (cluster box) node."
  [node]
  (contains? container-types (:type node)))

(defn container-kind
  "Which container a parsed ENTITY node belongs to, or nil when it stays loose
   (anything without a group/source/model id). Model nodes only group once their
   registry model is resolved onto `:group-id` (see `assign-model-groups`).

   Tables have no group of their own — they join their producer's box instead, via
   the `:container-id` stamped on by `assign-table-containers`."
  [node]
  (let [group-id (get-in node [:data :group-id])]
    (when-not (str/blank? group-id)
      (case (:type node)
        "transform" :transform-group
        "extract"   :source
        "model"     :model-group
        nil))))

(defn- box-id-of
  "The container box id a parsed node belongs to, or nil when it stays loose. Either
   its own group/source/model box, or — for a table — the `:container-id` its
   producer's box was stamped on by `assign-table-containers`."
  [node]
  (or (get-in node [:data :container-id])
      (some-> (container-kind node) (container-id (get-in node [:data :group-id])))))

(defn- boxed?
  "True when the node lays out inside a container box."
  [node]
  (some? (box-id-of node)))

(defn- container-of
  "The ELK container a parsed node lays out within: its box id, or the node's own id
   when it's loose (analytics models, group-less nodes, source tables)."
  [node]
  (or (box-id-of node) (:id node)))

(defn- assign-table-containers
  "Box each table with whatever WRITES it: stamp `[:data :container-id]` with the
   producer's box id, so a transform (or extract) and its output table land inside
   the same cluster box.

   Without this every transform→table edge crosses a box boundary and becomes a
   box→box edge at the root (see `build-elk-input`). ELK then stacks the box's
   transforms vertically and routes each one's edge out of the box and around it to
   a table column on the right — the reason a grouped graph with tables shown reads
   as edges doubling back across the transform nodes.

   A table stays loose when nothing in the graph produces it (a source table), when
   its producer is loose, or when producers in DIFFERENT boxes write it — there is
   no single box it belongs in, and forcing one would drag an unrelated box's
   lineage inside."
  [parsed-nodes parsed-edges]
  (let [box-of    (into {} (keep (fn [n] (when-let [box (box-id-of n)] [(:id n) box])))
                        parsed-nodes)
        table-id? (into #{} (comp (filter #(= "table" (:type %))) (map :id)) parsed-nodes)
        ;; table id -> set of its producers' boxes (nil for a loose producer)
        boxes     (reduce (fn [m {:keys [source target]}]
                            (cond-> m
                              (table-id? target) (update target (fnil conj #{}) (box-of source))))
                          {} parsed-edges)]
    (mapv (fn [node]
            (let [producer-boxes (get boxes (:id node))]
              (if (and (= 1 (count producer-boxes)) (first producer-boxes))
                (assoc-in node [:data :container-id] (first producer-boxes))
                node)))
          parsed-nodes)))


;; =============================================================================
;; Sizing
;; =============================================================================

(defn size-node
  "Stamp the node's ELK/xyflow dimensions plus the `:in-container?` and
   `:last-run-row?` flags the card reads to decide which rows to render. `groups?`
   and `no-run-history` must be the same values passed to `do-layout`, so height and
   rendered rows agree. `:node-width` overrides the default card width (the
   density's, when it sets one); a nil falls back, so callers can pass it through
   unconditionally."
  [{:keys [groups? table-shape no-run-history] card-width :node-width :or {table-shape :card}} node]
  (let [entity?       (contains? entity-types (:type node))
        ;; ENTITIES only: the flag means "the box heading already names your group,
        ;; drop the group row", which is true of a boxed transform/extract/model and
        ;; not of a boxed table — a table is boxed with its producer, and its subtitle
        ;; is a schema the heading doesn't name. Stamping it on a table would read as
        ;; permission to drop a row that has to stay.
        in-container? (boolean (and groups? entity? (boxed? node)))
        ;; A type with no execution history in this view drops the last-run row, so
        ;; the height has to drop with it — the card reads the flag back off `:data`.
        last-run-row? (not (contains? no-run-history (:type node)))
        pill?         (and (not entity?) (= :pill table-shape))
        width         (if pill? pill-size (or card-width node-width))
        height        (cond
                        pill?         pill-size
                        ;; a boxed entity gets its group name from the box heading,
                        ;; so it drops the group row
                        in-container? (if last-run-row? two-row-height one-row-height)
                        entity?       (if last-run-row? three-row-height two-row-height)
                        ;; table card: title + schema. A table's subtitle is its
                        ;; schema, not a group name, so being boxed costs it no row.
                        :else         two-row-height)]
    (-> node
        (assoc :width width :height height :style {:width width :height height}
               ;; without it xyflow drops handleBounds each update and stops culling
               :measured {:width width :height height})
        (assoc-in [:data :in-container?] in-container?)
        (assoc-in [:data :last-run-row?] last-run-row?))))


;; =============================================================================
;; Model grouping
;; =============================================================================

(defn model-id->registry-model
  "Map of `model-id -> registry-model` for every analytics-model instance loaded
   into DataScript. Empty until `:analytics-models.api/fetch-models` resolves, so
   model nodes stay loose until then (a re-layout fires when instances arrive)."
  []
  (into {} (datascript/q '[:find ?id ?registry
                           :where
                           [?e :analytics-model/model-id ?id]
                           [?e :analytics-model/registry-model ?registry]]
                         @conn)))

(defn- assign-model-groups
  "Give each model node the `:group-id` of its registry model so `container-kind`
   can box models that share a registry model together. Models whose instance
   isn't loaded (or has no registry model) keep their blank group-id and stay loose."
  [parsed-nodes model->registry]
  (mapv (fn [node]
          (if-let [registry (and (= "model" (:type node))
                                 (get model->registry (get-in node [:data :metadata :model-id])))]
            (assoc-in node [:data :group-id] registry)
            node))
        parsed-nodes))


;; =============================================================================
;; Extract direction
;; =============================================================================

(defn- exporter-extract-ids
  "Ids of the extract nodes that EXPORT out of the warehouse rather than ingest into
   it — the ones that end a pipeline instead of starting it, so nothing about the
   layout should treat them as its left edge.

   Read off the EDGES rather than a flag on the node, because the DAG payload carries
   no `is-exporter` in `:metadata`: the backend already reverses an exporter's edges
   (`table → extract`, against `extract → table` for an ingest extract), so an
   extract that is only ever an edge target is a sink.

   Requiring no outgoing edge keeps a read-AND-write extract on the source side: it
   still feeds the pipeline, so it stays a source.

   Note this is the only thing holding the constraint back — ELK will NOT complain.
   Its `EdgeAndLayerConstraintEdgeReverser` runs before the FIRST_SEPARATE validity
   check and REVERSES a constrained node's incoming edges, so the check never sees
   one. A wrongly-pinned exporter therefore lays out silently, with the reversed edge
   drawn backwards across the graph — the exact defect this removes.

   Hiding tables doesn't change the answer: `bypass-tables` / `contract-tables`
   reconnect a producer to its consumers through the dropped table without flipping
   an edge, so an exporter arrives here as `transform → extract`."
  [parsed-nodes parsed-edges]
  (let [extract-id? (into #{} (comp (filter #(= "extract" (:type %))) (map :id)) parsed-nodes)
        writes      (into #{} (comp (map :source) (filter extract-id?)) parsed-edges)
        reads       (into #{} (comp (map :target) (filter extract-id?)) parsed-edges)]
    (into #{} (remove writes) reads)))


;; =============================================================================
;; ELK input
;; =============================================================================

(defn- connected-components
  "Undirected connected components over `ids`, given `adj` (id → set of neighbour ids).
   Returns a seq of id-sets; ids absent from `adj` form singleton components."
  [ids adj]
  (loop [remaining (set ids) comps []]
    (if-let [start (first remaining)]
      (let [reachable (loop [stack [start] seen #{}]
                        (if-let [n (peek stack)]
                          (if (seen n)
                            (recur (pop stack) seen)
                            (recur (into (pop stack) (adj n)) (conj seen n)))
                          seen))]
        (recur (reduce disj remaining reachable) (conj comps reachable)))
      comps)))

(defn- ->elk-node
  "The slice of a node ELK actually needs. The rest — `:data` above all, which carries
   every entity's title, subtitle and metadata — is rejoined by id on the way back
   (see `flatten-layout`) rather than walked into the worker and out again."
  [{:keys [id width height layoutOptions]}]
  {:id            id
   :width         width
   :height        height
   :layoutOptions (or layoutOptions {})})

(defn- ->elk-edge
  "Likewise for edges: ELK routes on the endpoints, and `do-layout` returns the
   ORIGINAL edges rather than ELK's routed ones."
  [{:keys [id source target]}]
  {:id id :source source :target target})

(defn- build-elk-input
  "Bleene-style hierarchical input: each group/source box holds its members plus the
   edges *between* those members; edges that cross box boundaries become group→group
   edges at the root. This keeps every edge within one hierarchy level.

   When `:separate-groups?` is set, every root-level component that contains no source
   (a box holding an ingest extract, or a loose one) is dropped into a later ELK
   partition, so those `separated` boxes lay out to the RIGHT of every source-fed
   pipeline. An exporter doesn't count as one: it ends a pipeline rather than feeding
   it, so a component whose only extract is an exporter is not source-fed.

   With `groups?` false every node is treated as loose (no container boxes), so ELK
   lays the graph out flat — the same machinery, minus the clustering."
  [parsed-nodes parsed-edges {:keys [root group source-extra separate-groups?]} groups?]
  (let [box-of       (into {} (map (juxt :id (if groups? container-of :id))) parsed-nodes)
        grouped?     (if groups? boxed? (constantly false))
        root-edges   (->> parsed-edges
                          (keep (fn [{:keys [source target]}]
                                  (let [s (box-of source) t (box-of target)]
                                    (when (not= s t)
                                      {:id (str s "~>" t) :source s :target t}))))
                          distinct)
        ;; The boxes that START a pipeline, keyed by the box a node lays out in (its
        ;; own id when loose), so boxed and loose extracts are recognised by the same
        ;; lookup. Only INGEST extracts count — an exporter ends a pipeline, and gets
        ;; no layer constraint at all. A source holding BOTH kinds still counts: it
        ;; does feed the pipeline.
        ;; `box-of` is keyed by node ID, so map each node through `:id` first — handing
        ;; it the node MAP silently yields nil for every node and empties the set.
        exporter?    (exporter-extract-ids parsed-nodes parsed-edges)
        ingest-src?  (into #{} (comp (filter #(and (= "extract" (:type %))
                                                  (not (exporter? (:id %)))))
                                     (keep (comp box-of :id)))
                           parsed-nodes)
        ;; Source-less components go last. "Source-bearing" = a box (or loose node)
        ;; holding an INGEST extract; any undirected component (over cross-box edges)
        ;; with no such member is `separated` → ELK partition 1, vs 0 for sources and
        ;; every source-fed box.
        separated    (when separate-groups?
                       (let [adj (reduce (fn [m {:keys [source target]}]
                                           (-> m (update source (fnil conj #{}) target)
                                               (update target (fnil conj #{}) source)))
                                         {} root-edges)]
                         (into #{}
                               (comp (remove (fn [c] (some ingest-src? c))) cat)
                               (connected-components (distinct (vals box-of)) adj))))
        part-opts    (fn [id] (when separate-groups?
                                {"elk.partitioning.partition" (if (separated id) "1" "0")}))
        box-opts     (fn [box] (merge group
                                      (when (ingest-src? box) source-extra)
                                      (part-opts box)))
        box-edges    (reduce (fn [m edge]
                               (let [box (box-of (:source edge))]
                                 (if (and box (= box (box-of (:target edge))))
                                   (update m box (fnil conj []) (->elk-edge edge))
                                   m)))
                             {} parsed-edges)
        containers   (->> (filter grouped? parsed-nodes)
                          (group-by container-of)
                          (map (fn [[box members]]
                                 {:id            box
                                  :layoutOptions (box-opts box)
                                  :children      (mapv ->elk-node members)
                                  :edges         (get box-edges box [])})))
        ;; `source-extra` (e.g. the "sources left" layer constraint) targets source
        ;; boxes; apply it to loose ingest extracts too, in case some aren't boxed.
        loose        (->> (remove grouped? parsed-nodes)
                          (mapv (fn [n]
                                  (cond-> (->elk-node n)
                                    (and source-extra (ingest-src? (:id n)))
                                    (update :layoutOptions merge source-extra)
                                    separate-groups?
                                    (update :layoutOptions merge (part-opts (:id n)))))))]
    {:id            "root"
     :layoutOptions root
     :children      (vec (concat containers loose))
     :edges         (vec root-edges)}))


;; =============================================================================
;; ELK output → xyflow
;; =============================================================================

;; ELK's result is read as JS, never `js->clj`-ed: that walked the whole reply,
;; interning a keyword per key, for the five numbers per node the callers below keep.

(defn- position [^js n]
  {:x (j/get n :x) :y (j/get n :y)})

(defn- ->container-node [^js n]
  ;; `id` is the source of truth for kind/group-id ("transform-group:<id>" /
  ;; "source:<id>") — just as well, now that ELK is sent no custom properties.
  (let [id     (j/get n :id)
        width  (j/get n :width)
        height (j/get n :height)
        [kind group-id] (str/split id #":" 2)]
    {:id         id
     :type       kind
     :position   (position n)
     :width      width
     :height     height
     :style      {:width width :height height}
     ;; see `size-node`
     :measured   {:width width :height height}
     :selectable false
     :draggable  false
     :data       {:kind kind :group-id group-id}}))

(defn- rejoin
  "The pre-layout node ELK's result is describing. Unreachable today — every leaf ELK
   sees came from `parsed-nodes` — but the id is all ELK sends back, so an id that
   went missing would otherwise render as a typeless, sizeless, dataless node.
   `width`/`height` come from here rather than from ELK's reply: equivalent while
   `size-node` fixes every leaf's size, which a preset setting `nodeSize.constraints`
   would break."
  [by-id id]
  (or (get by-id id)
      (do (js/console.warn "[pipeline-dag layout] ELK returned an unknown node id:" id)
          {:id id})))

(defn- ->child-node [by-id parent-id ^js n]
  ;; ELK child coordinates are relative to the parent — exactly what xyflow
  ;; expects for a node with `:parentId` + `:extent "parent"`.
  (let [id (j/get n :id)]
    (assoc (rejoin by-id id)
           :parentId parent-id
           :extent   "parent"
           :position (position n))))

(defn- ->loose-node [by-id ^js n]
  (assoc (rejoin by-id (j/get n :id)) :position (position n)))

(defn- flatten-layout
  "ELK's laid-out graph -> xyflow nodes, rejoined against `by-id` (`{id parsed-node}`).
   Emits each container immediately followed by its children — xyflow requires a
   parent to precede them."
  [^js g by-id]
  (->> (j/get g :children)
       (mapcat (fn [^js child]
                 (let [kids (j/get child :children)]
                   ;; leaf nodes come back with no `children` key at all
                   (if (and kids (pos? (alength kids)))
                     (cons (->container-node child)
                           (map #(->child-node by-id (j/get child :id) %) kids))
                     [(->loose-node by-id child)]))))
       vec))

(defn- left-align-layers
  "ELK centres different-width boxes within a layer, so a column of group boxes
   ends up with a ragged left edge. Snap every root-level node (containers + loose
   nodes; children are positioned relative to their parent and ride along) to the
   left edge of its layer.

   Layers are recovered by scanning root nodes left-to-right and grouping those
   whose horizontal extents overlap: ELK always separates adjacent layers by
   `nodeNodeBetweenLayers`, so a node starting at/after the running right edge
   opens a new layer."
  [nodes]
  (let [roots      (->> nodes (remove :parentId) (sort-by #(get-in % [:position :x])))
        left-by-id (:acc
                    (reduce
                     (fn [{:keys [left right acc]} n]
                       (let [x (get-in n [:position :x])
                             r (+ x (or (:width n) 0))]
                         (if (and right (< x right))
                           {:left left :right (max right r) :acc (assoc acc (:id n) left)}
                           {:left x :right r :acc (assoc acc (:id n) x)})))
                     {:left nil :right nil :acc {}}
                     roots))]
    (mapv (fn [n]
            (if-let [lx (and (not (:parentId n)) (left-by-id (:id n)))]
              (assoc-in n [:position :x] lx)
              n))
          nodes)))


;; =============================================================================
;; Entry point
;; =============================================================================


(defn do-layout
  "Lay out an already-parsed `{:nodes :edges}` graph. Returns a promise of
   `{:nodes :edges}` ready for xyflow.

   Options:
     :preset          key into `layout-presets` (default `default-layout`)
     :density         key into `densities` (default `default-density`)
     :groups?         cluster into group/source/model container boxes
     :table-shape     :card (default) or :pill — see `size-node`
     :no-run-history  set of node types whose cards drop the last-run row — see
                      `size-node`
     :model->registry `model-id -> registry-model` map, so models sharing a
                      registry model box together (only used with `:groups?`)
     :user-id         whose layout cache to use"
  [{:keys [nodes edges]} {:keys [preset density groups? table-shape no-run-history model->registry user-id]}]
  (let [density*     (get densities (or density default-density)
                          (get densities default-density))
        preset*      (-> (get layout-presets (or preset default-layout)
                              (get layout-presets default-layout))
                         (update :root merge (:root density*))
                         (update :group merge (:group density*))
                         ;; flat, partitioning drags every sink up to the partition
                         ;; boundary: a 2.8k-node graph went from 9s to 12min
                         (cond-> (not groups?)
                           (-> (assoc :separate-groups? false)
                               (assoc-in [:root "elk.partitioning.activate"] "false"))))
        parsed-edges (vec edges)
        ;; Both grouping passes fill in what `size-node`'s `boxed?` check reads, so
        ;; they run first: model grouping stamps `:group-id`, then table boxing
        ;; stamps `:container-id` — and it reads the models' boxes, so its pass has
        ;; to come second.
        parsed-nodes (timing/timed
                      :parse
                      #(->> (cond-> (vec nodes)
                              groups? (-> (assign-model-groups model->registry)
                                          (assign-table-containers parsed-edges)))
                            (mapv (fn [n]
                                    (size-node {:groups?        groups?
                                                :table-shape    table-shape
                                                :no-run-history no-run-history
                                                :node-width     (:node-width density*)}
                                               n)))))
        input        (timing/timed
                      :elk-input
                      #(build-elk-input parsed-nodes parsed-edges preset* groups?))
        ;; What the worker is not sent, keyed for the rejoin on the way back.
        by-id        (into {} (map (juxt :id identity)) parsed-nodes)]
    (timing/start! :elk)
    (-> (layout-cache/through user-id input #(.layout elk (clj->js input) #js {}))
        (.then (fn [g]
                 (timing/end! :elk)
                 ;; xyflow's bezier edge renderer computes its own path from the
                 ;; handle positions, so we keep the parsed edges as-is.
                 (timing/timed
                  :flatten
                  #(hash-map :nodes (cond-> (flatten-layout g by-id)
                                      (:align-left? preset*) left-align-layers)
                             :edges (vec edges))))))))
